import React, { useState } from "react";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchInvoiceReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";

export default function InvoiceReport() {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerateReport = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Set start date to beginning of day (00:00:00)
      const startDateTime = new Date(startDate);
      startDateTime.setHours(0, 0, 0, 0);

      // Set end date to end of day (23:59:59)
      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999);

      const response = await dispatch(
        fetchInvoiceReport({
          startDate: startDateTime.toISOString(),
          endDate: endDateTime.toISOString(),
        })
      ).unwrap();

      if (!response || !response.data) {
        throw new Error("Invalid response data received");
      }

      setReportData(response.data);
    } catch (err) {
      setError(
        "Failed to generate report: " + (err.message || "Unknown error")
      );
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadExcel = () => {
    if (
      !reportData ||
      !reportData.invoices ||
      reportData.invoices.length === 0
    ) {
      setError("No data to download");
      return;
    }

    // Format data for Excel
    const worksheetData = reportData.invoices.map((invoice) => {
      // Handle multiple services by joining them with commas
      const serviceDescriptions = invoice.services
        ? invoice.services.map((service) => service.description).join(", ")
        : "";

      const serviceNotes = invoice.services
        ? invoice.services.map((service) => service.notes || "").join(", ")
        : "";

      return {
        "Service Date": new Date(invoice.serviceDate).toLocaleDateString(),
        Clinician: `${invoice.clinician.firstName} ${invoice.clinician.lastName}`,
        Client: `${invoice.client.firstName} ${invoice.client.lastName}`,
        "Invoice ID": invoice.invoiceId,
        Services: serviceDescriptions,
        "Service Item Notes": serviceNotes,
        Notes: invoice.notes || "",
        Amount: `$${invoice.amount.toFixed(2)}`,
      };
    });

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");

    // Generate filename with date range
    const startDateStr = startDate.toLocaleDateString().replace(/\//g, "-");
    const endDateStr = endDate.toLocaleDateString().replace(/\//g, "-");
    const fileName = `invoice-report-${startDateStr}-to-${endDateStr}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, fileName);
  };

  return (
    <div className="p-4">
      <div className="bg-white rounded-sm shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Invoice Report</h2>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                className="w-full p-2 border rounded"
              />
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <button
            onClick={handleGenerateReport}
            disabled={isLoading}
            className={`w-full py-2 px-4 border border-transparent rounded-sm shadow-sm text-sm font-medium text-white 
              ${
                isLoading ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"
              }`}>
            {isLoading ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </div>

      {reportData && reportData.invoices && reportData.invoices.length > 0 && (
        <div className="bg-white rounded-sm shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">
              {reportData.invoices.length} Invoices Found
            </h3>
            <button
              onClick={downloadExcel}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              Download Excel
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clinician
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service Item Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.invoices.map((invoice, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(invoice.serviceDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.clinician.firstName} {invoice.clinician.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.client.firstName} {invoice.client.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice.invoiceId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {invoice.services
                        ? invoice.services
                            .map((service) => service.description)
                            .join(", ")
                        : ""}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {invoice.services
                        ? invoice.services
                            .map((service) => service.notes || "")
                            .join(", ")
                        : ""}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {invoice.notes || ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${invoice.amount.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {reportData &&
        (!reportData.invoices || reportData.invoices.length === 0) && (
          <div className="bg-white rounded-sm shadow p-6 text-center">
            <p className="text-gray-500">
              No invoices found for the selected date range.
            </p>
          </div>
        )}
    </div>
  );
}
